import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SERVICE_VALIDATION_ERRORS, serviceMaxChar, SERVICE_FORM_PARAM, nonSpaceCharacterRegex, variableRegex } from '../../constants';
import { IServiceDetailsValidationModel, InputElementModel } from '../../models/index';

const serviceId: InputElementModel = {
  name: 'serviceId',
  value: '',
  error: SERVICE_VALIDATION_ERRORS.serviceId,
  expression: /^[A-Z0-9.-]+$/,
  isValid: true,
  isTouched: false
};

const serviceName: InputElementModel = {
  name: 'serviceName',
  value: '',
  error: SERVICE_VALIDATION_ERRORS.serviceName,
  expression: /^[a-zA-Z]([a-zA-Z0-9-])*$/,
  isValid: true,
  isTouched: false
};

const endpoint: InputElementModel = {
  name: 'endpoint',
  value: '',
  error: SERVICE_VALIDATION_ERRORS.endPoint,
  expression: new RegExp(variableRegex, 'g'),
  isValid: true,
  isTouched: false
};

const serviceDescription: InputElementModel = {
  name: 'serviceDescription',
  value: '',
  expression: /^[a-zA-Z0-9](.*)/,
  error: SERVICE_VALIDATION_ERRORS.serviceDescription,
  isValid: true,
  isTouched: false
};

const authName: InputElementModel = {
  name: 'authName',
  value: '',
  error: SERVICE_VALIDATION_ERRORS.authName,
  expression: /^[a-zA-Z]([a-zA-Z0-9-])*$/,
  isValid: true,
  isTouched: false
};

const serviceType: InputElementModel = {
  name: 'serviceType',
  value: ''
};

const userName: InputElementModel = {
  name: 'userName',
  value: '',
  error: SERVICE_VALIDATION_ERRORS.userNameInvalid,
  isValid: true,
  isTouched: false
};

const password: InputElementModel = {
  name: 'password',
  value: '',
  error: SERVICE_VALIDATION_ERRORS.userNameInvalid,
  isValid: true,
  isTouched: false
};

const authUrl: InputElementModel = {
  name: 'authUrl',
  value: '',
  expression: new RegExp(variableRegex, 'g'),
  error: SERVICE_VALIDATION_ERRORS.authUrlInvalid,
  isValid: true,
  isTouched: false
};

const authType: InputElementModel = {
  name: 'authType',
  value: ''
};

const authMethod: InputElementModel = {
  name: 'authMethod',
  value: ''
};

const paramKey: InputElementModel = {
  name: 'paramKey',
  value: '',
  error: SERVICE_VALIDATION_ERRORS.invalidKey,
  isValid: true,
  isTouched: false
};

const paramValue: InputElementModel = {
  name: 'paramValue',
  value: '',
  error: SERVICE_VALIDATION_ERRORS.invalidValue,
  isValid: true,
  isTouched: false
};

const headerXml: InputElementModel = {
  name: 'headerXml',
  value: '',
  error: SERVICE_VALIDATION_ERRORS.invalidXML,
  isValid: true,
  isTouched: false
};

const namespace: InputElementModel = {
  name: 'namespace',
  expression: nonSpaceCharacterRegex,
  value: '',
  error: 'service_validation_error.invalidNamespace',
  isValid: true,
  isTouched: false
};

export const serviceDetailsFormParam: IServiceDetailsValidationModel = {
  serviceId,
  serviceName,
  endpoint,
  serviceDescription,
  authName,
  serviceType,
  userName,
  password,
  authUrl,
  authType,
  authMethod,
  paramKey,
  paramValue,
  headerXml,
  namespace
};

/**
 * Custom hook to handle Event Form Inputs
 */
export const useServiceDetailForm = () => {
  // Form values
  const [formData, setFormParamValues] = useState(structuredClone(serviceDetailsFormParam));
  const { t } = useTranslation();

  useEffect(() => () => {
    setFormParamValues(serviceDetailsFormParam);
  }, []);

  const checkValidity = (name: string, value: string) => {
    const keyName = name as keyof IServiceDetailsValidationModel;
    const expString = formData[name as keyof IServiceDetailsValidationModel].expression;
    const regExp = new RegExp(expString || '');
    if (value.length < 1 || (regExp && !regExp.test(value.toLowerCase()))) {
      setFormParamValues((previousState: IServiceDetailsValidationModel) => {
        const data = structuredClone(previousState);
        data[keyName].isValid = false;
        data[keyName].value = value;
        data[keyName].isTouched = true;
        return data;
      });
    } else {
      // set the error state empty or remove the error for service name input
      setFormParamValues((previousState: IServiceDetailsValidationModel) => {
        const data = structuredClone(previousState);
        data[keyName].isValid = true;
        data[keyName].value = value;
        data[keyName].isTouched = true;
        return data;
      });
    }
  };

  const getFormParam = (previousState: IServiceDetailsValidationModel, name: string, currLength: number, maxChars: number, value: string) => {
    const keyName = name as keyof IServiceDetailsValidationModel;
    const data = structuredClone(previousState);
    const dataName = data[keyName].name;
    const SERVICE_VALIDATION_ERRORS = {
      serviceIdRequired: t('service_validation_error.serviceIdRequired'),
      serviceNameRequired: t('service_validation_error.serviceNameRequired'),
      endPointRequired: t('service_validation_error.endPointRequired'),
      userNameRequired: t('service_validation_error.userNameRequired'),
      passwordRequired: t('service_validation_error.passwordRequired'),
      authUrlRequired: t('service_validation_error.authUrlRequired'),
      namespaceRequired: t('service_validation_error.namespaceRequired'),
      serviceId: t('service_validation_error.serviceId'),
      serviceName: t('service_validation_error.serviceName'),
      authName: t('service_validation_error.authName'),
      endPoint: t('service_validation_error.endPoint'),
      serviceDescription: t('service_validation_error.serviceDescription'),
      userNameInvalid: t('service_validation_error.userNameInvalid'),
      passwordInvalid: t('service_validation_error.passwordInvalid'),
      authUrlInvalid: t('service_validation_error.authUrlInvalid'),
      invalidKey: t('service_validation_error.invalidKey'),
      invalidValue: t('service_validation_error.invalidValue'),
      invalidXML: t('service_validation_error.invalidXML'),
      invalidNamespace: t('service_validation_error.invalidNamespace'),
    };

    if (currLength >= maxChars) {
      data[keyName].isValid = false;
      data[keyName].value = value;
      data[keyName].isTouched = true;
      data[keyName].error = t(
        'service_detail_form_validation_component.err_max_char_limit',
        { replace: { maxChars: maxChars - 1 } }
      );
    } else if (data[keyName].value === '') {
      switch (dataName) {
        case SERVICE_FORM_PARAM.serviceName:
          data[keyName].error = SERVICE_VALIDATION_ERRORS.serviceNameRequired;
          break;
        case SERVICE_FORM_PARAM.endpoint:
          data[keyName].error = SERVICE_VALIDATION_ERRORS.endPointRequired;
          break;
        case SERVICE_FORM_PARAM.userName:
          data[keyName].error = SERVICE_VALIDATION_ERRORS.userNameRequired;
          break;
        case SERVICE_FORM_PARAM.passWord:
          data[keyName].error = SERVICE_VALIDATION_ERRORS.passwordRequired;
          break;
        case SERVICE_FORM_PARAM.authUrl:
          data[keyName].error = SERVICE_VALIDATION_ERRORS.authUrlRequired;
          break;
        case SERVICE_FORM_PARAM.namespace:
          data[keyName].error = SERVICE_VALIDATION_ERRORS.namespaceRequired;
          break;
        default:
          data[keyName].error = '';
          data[keyName].isValid = true;
      }
    } else {
      switch (dataName) {
        case SERVICE_FORM_PARAM.serviceId:
          data[keyName].error = SERVICE_VALIDATION_ERRORS.serviceId;
          break;
        case SERVICE_FORM_PARAM.serviceName:
          data[keyName].error = SERVICE_VALIDATION_ERRORS.serviceName;
          break;
        case SERVICE_FORM_PARAM.authName:
          data[keyName].error = SERVICE_VALIDATION_ERRORS.authName;
          break;
        case SERVICE_FORM_PARAM.endpoint:
          data[keyName].error = SERVICE_VALIDATION_ERRORS.endPoint;
          break;
        case SERVICE_FORM_PARAM.serviceDescription:
          data[keyName].error = SERVICE_VALIDATION_ERRORS.serviceDescription;
          break;
        case SERVICE_FORM_PARAM.userName:
          data[keyName].error = SERVICE_VALIDATION_ERRORS.userNameInvalid;
          break;
        case SERVICE_FORM_PARAM.passWord:
          data[keyName].error = SERVICE_VALIDATION_ERRORS.passwordInvalid;
          break;
        case SERVICE_FORM_PARAM.authUrl:
          data[keyName].error = SERVICE_VALIDATION_ERRORS.authUrlInvalid;
          break;
        case SERVICE_FORM_PARAM.paramKey:
          data[keyName].error = SERVICE_VALIDATION_ERRORS.invalidKey;
          break;
        case SERVICE_FORM_PARAM.paramValue:
          data[keyName].error = SERVICE_VALIDATION_ERRORS.invalidValue;
          break;
        case SERVICE_FORM_PARAM.headerXml:
          data[keyName].error = SERVICE_VALIDATION_ERRORS.invalidXML;
          break;
        case SERVICE_FORM_PARAM.namespace:
          data[keyName].error = SERVICE_VALIDATION_ERRORS.invalidNamespace;
          break;
        default:
          data[keyName].error = '';
      }
    }
    return data;
  };

  const getServiceFormValue = (oldValue: IServiceDetailsValidationModel, name: string, value: string) => {
    const keyName = name as keyof IServiceDetailsValidationModel;
    const data = structuredClone(oldValue);
    data[keyName].value = value;
    return data;
  };

  const recheckValidation = (serviceKeys: string[]) => {
    serviceKeys.forEach((key) => {
      const name = key as keyof IServiceDetailsValidationModel;
      if (formData[name]) {
        checkValidity(name, formData[name].value);
        setFormParamValues((previousState: IServiceDetailsValidationModel) => getFormParam(previousState, name, formData[name].value.length, +serviceMaxChar[name], formData[name].value));
      }
    });
  };

  // A method to handle form inputs
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const maxChars = event.target.maxLength > -1 ? event.target.maxLength : Number.MAX_SAFE_INTEGER;
    const currLength = event.target.value.length;

    if (formData[name as keyof IServiceDetailsValidationModel].expression) {
      checkValidity(name, value);
    }
    // To check if length exceeds maximum length defined
    if (currLength <= maxChars) {
      setFormParamValues((previousState: IServiceDetailsValidationModel) => getFormParam(previousState, name, currLength, maxChars, value));
    }
    // validate(event, name, value);
    // Let's set these values in state
    setFormParamValues((oldValue: IServiceDetailsValidationModel) => getServiceFormValue(oldValue, name, value));
  };
  return {
    handleInputChange,
    formData,
    setFormParamValues,
    recheckValidation,
  };
};
